/**
 * Reads the latest PerformanceResourceTiming entry for the given URL and
 * derives a breakdown of where the surrounding span's wall time was spent.
 *
 * Investigation context: Sentry `http.client` spans for `NinjaService/Register`
 * reach the multi-second range in the dashboard while the GCP HTTPS LB
 * caps the same call at ~0.75s end-to-end. The remaining time has to be
 * between fetch response arrival and the awaiting microtask actually
 * running. PerformanceResourceTiming gives us the network-phase timestamps;
 * subtracting them from the span's own wall time names that residual.
 *
 * Returned keys (only present when a matching resource entry exists):
 * - perf.fetch_total_ms — startTime → responseEnd, the browser's view of
 *                         the network fetch (DNS + connect + TLS + request
 *                         + response)
 * - perf.network_ms     — requestStart → responseStart, the round trip
 *                         from sending the request to receiving the first
 *                         response byte
 * - perf.download_ms    — responseStart → responseEnd, the response body
 *                         transfer
 * - perf.transfer_size  — bytes over the wire
 * - perf.post_fetch_ms  — spanWallMs - fetch_total_ms; the time between
 *                         the fetch completing and the awaiting microtask
 *                         resolving. A large value here is direct evidence
 *                         of main-thread / event-loop blocking after the
 *                         response was already on the wire.
 *
 * Returns an empty object when the Performance API or a matching entry is
 * unavailable (e.g. in non-browser environments or before the entry buffer
 * has been populated for this URL).
 */
export function getResourceTimings(
  url: string,
  spanWallMs: number
): Record<string, number> {
  if (
    typeof performance === 'undefined' ||
    typeof performance.getEntriesByName !== 'function'
  ) {
    return {};
  }
  const entries = performance.getEntriesByName(url, 'resource');
  if (entries.length === 0) return {};
  // Take the most recent entry. If two RPCs to the same URL overlap, this
  // returns the later one — but in this codebase Register fires once per
  // pageload and other unary RPCs fire serially, so collisions are rare.
  const r = entries[entries.length - 1] as PerformanceResourceTiming;

  const fetchTotalMs = r.responseEnd - r.startTime;

  // Without `Timing-Allow-Origin` on the cross-origin response, the
  // browser zero-fills request/response phase timestamps per the
  // Resource Timing spec. `startTime`, `responseEnd`, and `transferSize`
  // remain populated. Detect that case via `requestStart === 0` and
  // omit the per-phase metrics rather than emit misleading zeros /
  // inflated download values. `srvy.ninja` does not currently send TAO
  // — adding `Timing-Allow-Origin: *` (or the survey origin) on the
  // backend would unlock these phase numbers.
  const taoPresent = r.requestStart > 0 && r.responseStart > 0;

  const result: Record<string, number> = {
    'perf.fetch_total_ms': fetchTotalMs,
    'perf.transfer_size': r.transferSize ?? 0,
    // Clamp at 0 — performance.now() and resource timestamps come from
    // the same monotonic clock, but rounding can produce a small
    // negative.
    'perf.post_fetch_ms': Math.max(0, spanWallMs - fetchTotalMs),
    'perf.tao_present': taoPresent ? 1 : 0,
  };

  if (taoPresent) {
    result['perf.network_ms'] = r.responseStart - r.requestStart;
    result['perf.download_ms'] = r.responseEnd - r.responseStart;
  }

  return result;
}
